import axios, { AxiosResponse } from 'axios'
import {
  getAuthenticatedRequestConfig,
  useAxios,
} from '../../../../utils/apiUtils'
import { AllowedHttpMethods, resumeMatcherApiConfig } from '../../apiList'
import {
  ResumeMatchingData,
  SaveResumeMatchingDataResponse,
  UploadResumesResponse,
  GetAllResumesMatchingDataParams,
  GetAllResumesMatchingDataResponse,
} from '../../../../types/Recruitment/ResumeMatcher/resumeMatcherTypes'

export interface ResumeMatchDetailedAnalysis {
  strengths: string[]
  gaps: string[]
  recommendations: string[]
}

export interface ResumeMatchResultInner {
  matchScore: number
  overallMatch: string
  matchedSkills: string[]
  missingSkills: string[]
  experienceMatch: string
  detailedAnalysis: ResumeMatchDetailedAnalysis
  phoneNumber: string
  CandidateName: string
  email: string
  matchScoreBasis: string
  linkedInId: string
  dateOfBirth: string
  country: string
  currentLocation: string
  allResumeSkills: string[]
  experienceYearsResume: string
  questionsFromResume: QuestionsFromResume1[]
  questionsFromJobDescription: QuestionsFromJobDescription2[]
}

export interface ResumeMatchSingleFileResult {
  filename: string
  status: 'success' | 'failed'
  matchResult?: ResumeMatchResultInner
  [key: string]: unknown
}

export interface ResumeMatchBatchResponseShape {
  totalFiles: number
  processedFiles: number
  failedFiles: number
  results: ResumeMatchSingleFileResult[]
  errors: unknown[]
  status: string
  [key: string]: unknown
}

export interface QuestionsFromResume1 {
  question: string
  expectedAnswer: string
}

export interface QuestionsFromJobDescription2 {
  question: string
  expectedAnswer: string
}

export interface QuestionsFromResume {
  question: string
  answer: string
}

export interface QuestionsFromJobDescription {
  question: string
  answer: string
}

const token = 'bc9091fb-3371-483f-879b-c43a2dcd3264' //remove this line in production
//const token = process.env.REACT_APP_AI_RESUME_TOKEN  use this in production
//const token = process.env.REACT_APP_AI_RESUME_TOKEN

export const matchResumesBatch = (prepareObject: {
  file: FormData
}): Promise<AxiosResponse<ResumeMatchBatchResponseShape>> => {
  const requestConfig = {
    url: resumeMatcherApiConfig.matchResumesBatch,
    method: 'post' as const,
    data: prepareObject.file,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  }

  return axios(requestConfig)
}

export const addResumesMatchingData = async (
  data: ResumeMatchingData[],
): Promise<SaveResumeMatchingDataResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: resumeMatcherApiConfig.addResumesMatchingData,
    method: AllowedHttpMethods.post,
    data,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

export const uploadResumes = async (
  file: File[],
  jobTitle: string,
  isFolder = false, //change this if folder is implemented in UI
): Promise<UploadResumesResponse> => {
  const formData = new FormData()

  // Add parameters
  formData.append('isFolder', isFolder.toString())
  formData.append('jobTitle', jobTitle)

  // Add files
  file.forEach((file) => {
    formData.append('file', file, file.name)
  })

  const requestConfig = getAuthenticatedRequestConfig({
    url: resumeMatcherApiConfig.uploadResumes,
    method: AllowedHttpMethods.post,
    data: formData,
    additionalHeaders: {
      'Content-Type': 'multipart/form-data',
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

export const getAllResumesMatchingData = async (
  params: GetAllResumesMatchingDataParams,
): Promise<GetAllResumesMatchingDataResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: resumeMatcherApiConfig.getAllResumesMatchingData,
    method: AllowedHttpMethods.get,
    params: {
      jobTitle: params.jobTitle,
      isSelected: params.isSelected,
      createdDate: params.createdDate,
      startIndex: params.startIndex ?? undefined,
      endIndex: params.endIndex ?? undefined,
      from: params.from,
      to: params.to,
      matchScore: params.matchScore,
      recruiterName: params.recruiterName ?? undefined,
      HrFollowupStatus: params.HrFollowupStatus ?? undefined,
      isExternalCreated: params.isExternalCreated ?? undefined,
      vendorName: params.vendorName ?? undefined,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

export const exportResumesMatchingData = async (
  params: GetAllResumesMatchingDataParams,
): Promise<Blob | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: resumeMatcherApiConfig.exportResumesMatchingData,
    method: AllowedHttpMethods.get,
    params: {
      jobTitle: params.jobTitle,
      isSelected: params.isSelected,
      createdDate: params.createdDate,
      startIndex: params.startIndex,
      endIndex: params.endIndex,
      from: params.from,
      to: params.to,
      matchScore: params.matchScore,
      recruiterName: params.recruiterName,
      HrFollowupStatus: params.HrFollowupStatus,
      isExternalCreated: params.isExternalCreated,
      vendorName: params.vendorName,
      token: localStorage.getItem('token') ?? '',
    },
    responseType: 'blob',
  })

  const response = await useAxios(requestConfig)
  return response.data
}

export const getExternalVendorsList = async (): Promise<string[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: resumeMatcherApiConfig.getExternalVendorCompanyNames,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

/**
 * Fetch resume file path by resume id.
 * The API is expected to return either a plain string path or an object { filePath: string }.
 */
export const getResumesPath = async (id: number): Promise<string> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: resumeMatcherApiConfig.getResumesPath,
    method: AllowedHttpMethods.get,
    params: { id },
  })

  const response = await useAxios(requestConfig)
  return response.data?.filePath || response.data || ''
}

export default {
  matchResumesBatch,
  addResumesMatchingData,
  uploadResumes,
  getAllResumesMatchingData,
  exportResumesMatchingData,
  getExternalVendorsList,
  getResumesPath,
}
